import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Pencil,
  Check,
  X,
  Database,
  ArrowUpRight,
  Play,
  MoreHorizontal,
  Trash2,
  ChevronDown,
} from 'lucide-react';
import { CampaignData } from './types';
import { CAMPAIGN_STATUS_COLORS } from '../../../constants/campaignStatus';

interface CampaignHeaderProps {
  campaign: CampaignData;
  isEditingName: boolean;
  editName: string;
  showMoreMenu: boolean;
  onEditNameChange: (value: string) => void;
  onStartEditName: () => void;
  onSaveName: () => void;
  onCancelNameEdit: () => void;
  onToggleStatus: () => void;
  onToggleMoreMenu: () => void;
  onDelete: () => void;
}

export const CampaignHeader: React.FC<CampaignHeaderProps> = ({
  campaign,
  isEditingName,
  editName,
  showMoreMenu,
  onEditNameChange,
  onStartEditName,
  onSaveName,
  onCancelNameEdit,
  onToggleStatus,
  onToggleMoreMenu,
  onDelete,
}) => {
  const navigate = useNavigate();
  const [showDatasetsDropdown, setShowDatasetsDropdown] = useState(false);

  return (
    <div className="h-14 sm:h-16 flex items-center justify-between px-3 sm:px-6 border-b border-neutral-200 flex-shrink-0">
      <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
        {isEditingName ? (
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={editName}
              onChange={(e) => onEditNameChange(e.target.value)}
              className="text-[13px] sm:text-[15px] font-semibold text-neutral-900 px-2 py-1 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-900 max-w-[150px] sm:max-w-none"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') onSaveName();
                if (e.key === 'Escape') onCancelNameEdit();
              }}
            />
            <button onClick={onSaveName} className="p-1 text-neutral-600 hover:bg-neutral-100 rounded">
              <Check className="w-4 h-4" />
            </button>
            <button onClick={onCancelNameEdit} className="p-1 text-neutral-400 hover:bg-neutral-100 rounded">
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <button
            onClick={onStartEditName}
            className="flex items-center gap-2 group min-w-0"
            aria-label={`Edit campaign name: ${campaign.name}`}
          >
            <span
              role="heading"
              aria-level={1}
              className="text-[13px] sm:text-[15px] font-semibold text-neutral-900 truncate"
            >
              {campaign.name}
            </span>
            <Pencil className="w-3 h-3 text-neutral-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
          </button>
        )}

        {campaign.datasets.length === 1 ? (
          <button
            onClick={() => navigate(`/datasets/${campaign.datasets[0].id}`)}
            className="hidden sm:flex items-center gap-1.5 px-2 py-1 text-[11px] text-neutral-500 hover:text-neutral-700 bg-neutral-100 hover:bg-neutral-200 rounded-md transition-colors max-w-[200px]"
            title={campaign.datasets[0].name}
          >
            <Database className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">{campaign.datasets[0].name}</span>
            <ArrowUpRight className="w-2.5 h-2.5 flex-shrink-0" />
          </button>
        ) : campaign.datasets.length > 1 ? (
          <div className="hidden sm:block relative">
            <button
              onClick={() => setShowDatasetsDropdown(!showDatasetsDropdown)}
              className="flex items-center gap-1.5 px-2 py-1 text-[11px] text-neutral-500 hover:text-neutral-700 bg-neutral-100 hover:bg-neutral-200 rounded-md transition-colors"
            >
              <Database className="w-3 h-3" />
              {campaign.datasets.length} datasets
              <ChevronDown className={`w-3 h-3 transition-transform ${showDatasetsDropdown ? 'rotate-180' : ''}`} />
            </button>
            {showDatasetsDropdown && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowDatasetsDropdown(false)} />
                <div className="absolute left-0 mt-1 w-56 bg-white border border-neutral-200 rounded-lg shadow-lg py-1 z-20">
                  {campaign.datasets.map((dataset) => (
                    <button
                      key={dataset.id}
                      onClick={() => {
                        navigate(`/datasets/${dataset.id}`);
                        setShowDatasetsDropdown(false);
                      }}
                      className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-[12px] text-neutral-700 hover:bg-neutral-50 transition-colors"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <Database className="w-3 h-3 text-neutral-400 flex-shrink-0" />
                        <span className="truncate">{dataset.name}</span>
                      </div>
                      <ArrowUpRight className="w-3 h-3 text-neutral-400 flex-shrink-0" />
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        ) : null}
      </div>

      <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
        <button
          onClick={campaign.status !== 'completed' ? onToggleStatus : undefined}
          disabled={campaign.status === 'completed'}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] sm:text-[11px] font-medium rounded-lg transition-colors ${
            campaign.status === 'active'
              ? 'bg-neutral-900 text-white hover:bg-neutral-800'
              : campaign.status === 'completed'
              ? `${CAMPAIGN_STATUS_COLORS.completed.bg} ${CAMPAIGN_STATUS_COLORS.completed.text} cursor-default`
              : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
          }`}
        >
          {campaign.status === 'active' ? (
            <>
              <span className={`w-1.5 h-1.5 rounded-full ${CAMPAIGN_STATUS_COLORS.active.dot} animate-pulse`} />
              Active
            </>
          ) : campaign.status === 'draft' ? (
            <>
              <Play className="w-3 h-3" />
              Draft
            </>
          ) : campaign.status === 'completed' ? (
            <>
              <span className={`w-1.5 h-1.5 rounded-full ${CAMPAIGN_STATUS_COLORS.completed.dot}`} />
              Completed
            </>
          ) : (
            <>
              <Play className="w-3 h-3" />
              Paused
            </>
          )}
        </button>

        <div className="relative">
          <button
            onClick={onToggleMoreMenu}
            className="p-2 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded-md"
          >
            <MoreHorizontal className="w-4 h-4" />
          </button>
          {showMoreMenu && (
            <>
              <div className="fixed inset-0 z-40" onClick={onToggleMoreMenu} />
              <div className="absolute right-0 mt-1 w-36 bg-white border border-neutral-200 rounded-lg shadow-lg py-1 z-50">
                <button
                  onClick={onDelete}
                  className="w-full flex items-center gap-2 px-3 py-2 text-[12px] text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Delete
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
